import { useColorScheme } from '@mui/material/styles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router'

import { useAuth } from '@/shared/auth/auth-context'
import { shellSlots, SlotActions, SlotPanels, useSlot } from '@/shared/registry'
import { AccountMenu, type AccountMenuItem, type ColorMode } from '@/shared/ui'
import { cn, focusRing } from '@/shared/ui/cn'
import { Moon, Sun } from '@/shared/ui/icons'

import { AppNav } from './AppNav'
import { AuthDisabledBanner } from './AuthDisabledBanner'
import { DeviceClockBanner } from './DeviceClockBanner'
import { useHeightProperty } from './height-property'
import { useSiteBranding, type SiteBranding, type SiteNavigationLink } from './site-branding'
import { useSiteName } from './site-name'

/**
 * The primary nav bar, on every page, from the mockups' `components/layout/PrimaryNav.jsx`: one
 * bar with the logo, the nav, then the theme button and the account menu, over the gradient nav line.
 * Sticky from tablet width up; on a phone it scrolls away, so a wrapped bar never covers the page.
 * The mockups' black `SecondaryNav` strip is retired in the mockups themselves: its links live in
 * this bar.
 */
export function AppHeader() {
  const { user } = useAuth()
  const headerRef = useRef<HTMLElement>(null)
  useHeightProperty(headerRef, headerHeightProperty)
  // The site's own logo and navigation links, signed in or out.
  const branding = useSiteBranding()

  return (
    <header ref={headerRef} className="z-40 bg-background/90 backdrop-blur-md md:sticky md:top-0">
      <AuthDisabledBanner />
      <DeviceClockBanner />
      <SlotPanels slot={shellSlots.headerBanners} />
      <div className="mx-auto flex min-h-[72px] max-w-[1600px] flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2 sm:px-6 md:flex-nowrap md:gap-x-8 md:py-0">
        <Logo logo={branding?.logo ?? null} />
        <AppNav links={branding?.navigationLinks ?? noLinks} />
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-3 sm:gap-4">
          {user !== null && <SlotActions slot={shellSlots.headerLeadingActions} />}
          {user !== null && <SlotActions slot={shellSlots.headerActions} />}
          <ThemeButton />
          {user !== null && <Account name={user.displayName} />}
        </div>
      </div>
      <div className="h-1.5 bg-[image:var(--tms-palette-navLine)]" />
    </header>
  )
}

/**
 * The custom property holding the header's height, so a sticky part of a page, such as Admin
 * Home's side menu, and the focused control (`index.css`) stay clear of it however tall banners
 * make it.
 */
export const headerHeightProperty = '--shell-header-height'

const noLinks: readonly SiteNavigationLink[] = []

/**
 * The site's own logo, at the size the site gives it, else the Skillable TMS lockup from the
 * mockups: the color one on light, the reverse one on dark; the `dark` variant picks, with no script.
 */
function Logo(props: { readonly logo: SiteBranding['logo'] }) {
  const intl = useIntl()
  const siteName = useSiteName()
  const { logo } = props
  const width = logo?.width ?? null
  const height = logo?.height ?? null
  // The site's own size; with none, the Skillable logo's height.
  const size = useMemo(
    () => ({ width: width ?? 'auto', height: height ?? (width === null ? 44 : 'auto') }),
    [width, height],
  )

  return (
    <Link
      to="/"
      aria-label={intl.formatMessage({ id: 'shell.header.home' })}
      className={cn('flex shrink-0 items-center rounded', focusRing)}
    >
      {logo === null ? <SkillableLogo /> : (
        <img
          src={logo.url}
          alt={siteName ?? ''}
          style={size}
          className="max-h-16 max-w-[240px] object-contain"
        />
      )}
    </Link>
  )
}

function SkillableLogo() {
  return (
    <>
      <img
        src="/skillable-tms-logo.png"
        alt="Skillable TMS"
        width={576}
        height={292}
        className="h-11 w-auto dark:hidden"
      />
      <img
        src="/skillable-tms-logo-reverse.png"
        alt="Skillable TMS"
        width={576}
        height={292}
        className="hidden h-11 w-auto dark:block"
      />
    </>
  )
}

/** The current scheme, resolving `system` to what the browser prefers. */
function useResolvedScheme() {
  const { mode, systemMode, setMode } = useColorScheme()
  const resolved: ColorMode = (mode === 'system' ? systemMode : mode) === 'dark' ? 'dark' : 'light'
  return { resolved, setMode }
}

/** The round light/dark button beside the account menu, as the mockup has. */
function ThemeButton() {
  const intl = useIntl()
  const { resolved, setMode } = useResolvedScheme()
  const isDark = resolved === 'dark'
  const toggle = useCallback(() => setMode(isDark ? 'light' : 'dark'), [isDark, setMode])

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={intl.formatMessage({ id: isDark ? 'shell.theme.toLight' : 'shell.theme.toDark' })}
      className={cn(
        'flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-line text-text-primary transition hover:border-primary-main hover:text-primary-main',
        focusRing,
      )}
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  )
}

/** The account menu: contributed links, then Sign Out, then the theme choice. */
function Account(props: { readonly name: string }) {
  const intl = useIntl()
  const { mode, signOut } = useAuth()
  const { resolved, setMode } = useResolvedScheme()
  const links = useSlot(shellSlots.accountMenu, 'link')
  const [signOutFailed, setSignOutFailed] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const onSignOut = useCallback(() => {
    // Success navigates away. A rejection means the user is still signed in, so say so.
    setSignOutFailed(false)
    signOut().catch(() => {
      setSignOutFailed(true)
    })
  }, [signOut])

  // The Sign Out item closed with the menu, so focus goes back to the menu's trigger.
  useEffect(() => {
    if (signOutFailed) {
      menuRef.current?.querySelector('button')?.focus()
    }
  }, [signOutFailed])

  const items = useMemo<AccountMenuItem[]>(() => {
    const entries: AccountMenuItem[] = links.map((link) => ({
      label: intl.formatMessage({ id: link.labelId }),
      href: typeof link.path === 'string' ? link.path : link.path({}),
    }))
    // Hidden in disabled mode, where `signOut` always rejects.
    if (mode !== 'disabled') {
      entries.push({
        label: intl.formatMessage({ id: 'shell.header.signOut' }),
        onSelect: onSignOut,
      })
    }
    return entries
  }, [intl, links, mode, onSignOut])

  const colorMode = useMemo(
    () => ({
      label: intl.formatMessage({ id: 'shell.theme.label' }),
      lightLabel: intl.formatMessage({ id: 'shell.theme.light' }),
      darkLabel: intl.formatMessage({ id: 'shell.theme.dark' }),
      mode: resolved,
      onChange: setMode,
    }),
    [intl, resolved, setMode],
  )

  return (
    <>
      {/* A long name, such as a username used when no name is known, wraps rather than
          running off a narrow screen; the green name keeps full contrast on hover; and the panel
          scrolls within the room below the sticky header. */}
      <div
        ref={menuRef}
        className="min-w-0 max-w-full [&>div>button]:max-w-full [&>div>button]:break-all [&>div>button]:text-left [&>div>button]:hover:opacity-100 [&>div>div]:overflow-y-auto [&>div>div]:overscroll-contain md:[&>div>div]:max-h-[calc(100dvh-var(--shell-header-height,0px)-1rem)]"
      >
        <AccountMenu name={props.name} items={items} colorMode={colorMode} />
      </div>
      {signOutFailed && (
        <p role="alert" className="text-sm text-error-text">
          {intl.formatMessage({ id: 'shell.header.signOutFailed' })}
        </p>
      )}
    </>
  )
}
